import React from "react";

const MAP = {
  green: { dot: "bg-emerald-500", label: "On Track", evidence: "Validated in resume", cls: "text-emerald-700" },
  yellow: { dot: "bg-amber-500", label: "Partial Evidence", evidence: "Partial / inferred", cls: "text-amber-600" },
  red: { dot: "bg-rose-500", label: "Needs Improvement", evidence: "Not found in resume", cls: "text-rose-600" },
};

const SkillGapPreview = ({ gap }) => {
  if (!gap) return null;
  const counts = gap.items.reduce((acc, it) => ({ ...acc, [it.status]: (acc[it.status] || 0) + 1 }), {});
  const total = gap.items.length || 1;
  const segs = [
    { key: "green", label: "On track", n: counts.green || 0, cls: "bg-emerald-500" },
    { key: "yellow", label: "Partial", n: counts.yellow || 0, cls: "bg-amber-500" },
    { key: "red", label: "Missing", n: counts.red || 0, cls: "bg-rose-400" },
  ];
  return (
    <div className="card-soft p-5 h-full" data-testid="skill-gap-container">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-lg font-bold text-slate-900">Skill Gap Preview</h3>
        <span className="text-xs text-slate-500">vs {gap.role_title}</span>
      </div>

      <div className="mt-3" data-testid="skill-gap-chart">
        <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
          {segs.map((s) => s.n ? <span key={s.key} className={s.cls} style={{ width: `${(s.n / total) * 100}%`, transition: "width 0.7s ease" }} /> : null)}
        </div>
        <div className="mt-2 flex flex-wrap gap-3">
          {segs.map((s) => (
            <span key={s.key} className="inline-flex items-center gap-1.5 text-xs text-slate-600" data-testid={`skill-gap-legend-${s.key}`}>
              <span className={`h-2 w-2 rounded-full ${s.cls}`} /><span className="num-display text-xs">{s.n}</span> {s.label}
            </span>
          ))}
        </div>
      </div>

      {/* Mobile: readable stacked cards (no cramped 4-column table) */}
      <ul className="mt-3 space-y-2 sm:hidden" data-testid="skill-gap-cards">
        {gap.items.map((it, i) => {
          const s = MAP[it.status] || MAP.red;
          return (
            <li key={i} className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={`skill-gap-card-${i}`}>
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm font-semibold text-slate-900 capitalize break-words">{it.skill}</p>
                <span className="inline-flex items-center gap-1.5 text-xs shrink-0">
                  <span className={`w-2 h-2 rounded-full ${s.dot}`} />
                  <span className={s.cls}>{s.label}</span>
                </span>
              </div>
              <p className="mt-1 text-xs text-slate-500">{it.type} · {s.evidence}</p>
            </li>
          );
        })}
      </ul>

      <div className="mt-3 hidden sm:block overflow-x-auto" data-testid="skill-gap-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left">
              <th className="pb-2 pr-3 text-[10px] font-mono uppercase tracking-wider text-slate-500">Skill</th>
              <th className="pb-2 pr-3 text-[10px] font-mono uppercase tracking-wider text-slate-500">Requirement</th>
              <th className="pb-2 pr-3 text-[10px] font-mono uppercase tracking-wider text-slate-500">Evidence</th>
              <th className="pb-2 text-[10px] font-mono uppercase tracking-wider text-slate-500">Status</th>
            </tr>
          </thead>
          <tbody>
            {gap.items.map((it, i) => {
              const s = MAP[it.status] || MAP.red;
              return (
                <tr key={i} className="border-t border-slate-200" data-testid={`skill-gap-row-${i}`}>
                  <td className="py-2 pr-3 text-slate-800 capitalize font-medium">{it.skill}</td>
                  <td className="py-2 pr-3 text-slate-500 text-xs">{it.type}</td>
                  <td className="py-2 pr-3 text-slate-500 text-xs">{s.evidence}</td>
                  <td className="py-2">
                    <span className="inline-flex items-center gap-1.5 text-xs" data-testid="skill-status-badge">
                      <span className={`w-2 h-2 rounded-full ${s.dot}`} />
                      <span className={s.cls}>{s.label}</span>
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-500 mt-2">Showing {gap.shown} of {gap.total} required & preferred skills</p>
    </div>
  );
};

export default SkillGapPreview;
